import { useMemo } from 'react'
import { useIntelligence } from '../../hooks/useIntelligence'
import { linearTrendForecast, movingAverage, seasonalIndices } from '../../lib/forecast'
import KpiCard from './KpiCard'
import LineChart from './charts/LineChart'
import BarChart from './charts/BarChart'
import Alert from '../ui/Alert'
import { fmtNum, fmtPct, fmtDays } from '../../lib/format'
import { kpiMeta } from '../../lib/kpiMeta'

interface Props { windowMonths?: number }

const MONTH_ABBR = ['', 'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']

function monthLabel(iso: string): string {
  const d = new Date(iso)
  return MONTH_ABBR[d.getUTCMonth() + 1] ?? ''
}

export default function InteligenciaPanel({ windowMonths = 6 }: Props) {
  const { data, loading, error } = useIntelligence(windowMonths)

  const derived = useMemo(() => {
    if (!data) return null

    const qtySeries = data.monthly.map(m => m.qty)
    const forecast = linearTrendForecast(qtySeries)
    const movAvg = movingAverage(qtySeries, 3)
    const lastMonth = qtySeries.at(-1) ?? 0
    const trendDir = forecast.slope > 0.5 ? 'alta' : forecast.slope < -0.5 ? 'baixa' : 'estável'

    const season = seasonalIndices(data.seasonality)
    const peakMonth = season.length ? season.reduce((a, b) => (b.index > a.index ? b : a)) : null
    const lowMonth = season.length ? season.reduce((a, b) => (b.index < a.index ? b : a)) : null

    // Sell-through agregado e por curva
    let totalSold = 0, totalStock = 0
    for (const r of data.sellThrough) { totalSold += r.sold; totalStock += r.stock }
    const sellThroughPct = totalSold + totalStock > 0 ? (totalSold / (totalSold + totalStock)) * 100 : 0

    return { forecast, movAvg, lastMonth, trendDir, season, peakMonth, lowMonth, sellThroughPct, totalSold, totalStock }
  }, [data])

  if (loading) {
    return <div className="flex items-center justify-center py-20"><div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" /></div>
  }
  if (error) return <Alert type="error" message={error} />
  if (!data || !derived) return null

  const hasMonthly = data.monthly.length >= 2
  const sellThroughMeta = kpiMeta({ value: derived.sellThroughPct, sentido: 'min', verde: 60, amarelo: 35, format: v => `${v}%` })

  const growing = data.trend.filter(t => (t.change_pct ?? 0) > 0).sort((a, b) => (b.change_pct ?? 0) - (a.change_pct ?? 0))
  const declining = data.trend.filter(t => (t.change_pct ?? 0) < 0).sort((a, b) => (a.change_pct ?? 0) - (b.change_pct ?? 0))

  return (
    <div className="flex flex-col gap-6">
      {/* ── KPIs ──────────────────────────────────────────────── */}
      <div className="flex flex-wrap gap-2 [&>*]:w-[220px]">
        {/* Forecast/Intervalo/Mês de pico: cards informativos (projeção/diagnóstico, sem meta) → sem selo. */}
        <KpiCard
          label="Forecast próximo mês"
          value={hasMonthly ? fmtNum(Math.round(derived.forecast.next)) : '—'}
          tone="info"
          hint={`tendência de ${derived.trendDir} · MM3: ${fmtNum(Math.round(derived.movAvg))}`}
          analysis="Projeção de unidades para o próximo mês pela tendência recente. Use para antecipar compras."
        />
        <KpiCard
          label="Sell-through"
          value={fmtPct(derived.sellThroughPct)}
          tone={sellThroughMeta.tone}
          meta={{ status: sellThroughMeta.status, label: sellThroughMeta.metaLabel }}
          hint={`${fmtNum(derived.totalSold)} vendidos / ${fmtNum(derived.totalStock)} em estoque`}
          analysis="% do que entrou que já foi vendido. ≥60% bom (giro saudável) · 35–60% atenção · <35% encalhe."
        />
        <KpiCard
          label="Intervalo de recompra"
          value={data.repurchase?.median_interval_days != null ? fmtDays(data.repurchase.median_interval_days) : '—'}
          tone="info"
          hint={data.repurchase ? `mediana · ${fmtNum(data.repurchase.products_with_repurchase)} SKUs recomprados` : 'sem dados'}
          analysis="Dias medianos entre recompras de um mesmo item. Menor = recompra mais frequente; pauta reposição e CRM."
        />
        <KpiCard
          label="Mês de pico"
          value={derived.peakMonth ? MONTH_ABBR[derived.peakMonth.month_num] : '—'}
          tone="info"
          hint={derived.peakMonth ? `índice ${derived.peakMonth.index.toFixed(2)}×` : 'sem dados'}
          analysis="Mês de maior sazonalidade histórica. Antecipe compra e estoque para essa época."
        />
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Forecast / série mensal */}
        <section className="bg-surface rounded-xl border border-line p-4 flex flex-col gap-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-faint">Vendas mensais e tendência</h3>
          {hasMonthly ? (
            <LineChart
              width={460}
              height={160}
              points={[
                ...data.monthly.map(m => ({ label: monthLabel(m.month), value: m.qty })),
                { label: 'prev', value: Math.round(derived.forecast.next) },
              ]}
              formatValue={v => fmtNum(v)}
            />
          ) : <p className="text-sm text-ink-faint">Histórico insuficiente para projeção.</p>}
          <p className="text-[11px] text-ink-faint">Unidades/mês · último ponto = forecast linear</p>
        </section>

        {/* Sazonalidade */}
        <section className="bg-surface rounded-xl border border-line p-4 flex flex-col gap-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-faint">Índice de sazonalidade</h3>
          {derived.season.length > 0 ? (
            <BarChart
              height={140}
              bars={derived.season.map(s => ({
                label: MONTH_ABBR[s.month_num],
                value: s.index,
                color: s.index >= 1.1 ? '#34d399' : s.index <= 0.9 ? '#f97316' : '#22d3ee',
              }))}
              formatValue={v => `${v.toFixed(2)}×`}
            />
          ) : <p className="text-sm text-ink-faint">Sem dados de sazonalidade.</p>}
          <p className="text-[11px] text-ink-faint">Índice por mês (1,0× = média) · verde acima, laranja abaixo</p>
        </section>
      </div>

      {/* ── Tendência SKU ─────────────────────────────────────── */}
      <div className="grid md:grid-cols-2 gap-5">
        <TrendTable title="Maior crescimento" rows={growing} positive />
        <TrendTable title="Maior queda" rows={declining} positive={false} />
      </div>
    </div>
  )
}

function TrendTable({ title, rows, positive }: { title: string; rows: { product_id: string; sku: string; name: string; change_pct: number | null }[]; positive: boolean }) {
  return (
    <section className="bg-surface rounded-xl border border-line p-4 flex flex-col gap-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-faint">{title}</h3>
      {rows.length === 0 ? (
        <p className="text-sm text-ink-faint">Sem dados.</p>
      ) : (
        <table className="w-full text-sm mobile-stack-table">
          <tbody>
            {rows.map(r => (
              <tr key={r.product_id} className="border-b border-line/50 last:border-0">
                <td className="py-1.5 pr-3 text-ink max-w-[220px] truncate" title={r.name}>{r.name}</td>
                <td className={`py-1.5 text-right font-semibold tabular-nums ${positive ? 'text-emerald-400' : 'text-red-400'}`}>
                  {r.change_pct != null ? `${r.change_pct > 0 ? '+' : ''}${r.change_pct.toFixed(0)}%` : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}
